import { CircleAlert } from 'lucide-react';

// Read-only view of a design's website classification (the `catalog` block the
// API returns from services/catalogue/catalog_view.py): one Catalog Category,
// its sub-categories, occasions, and the attributes, each with where it came from.

export const REVIEW_STYLE = {
  ok: 'bg-green-50 text-green-700 ring-green-200',
  approved: 'bg-sky-50 text-sky-700 ring-sky-200',
  needs_review: 'bg-amber-50 text-amber-800 ring-amber-200',
  unclassified: 'bg-gray-50 text-gray-500 ring-gray-200',
};

const SOURCE_TEXT = (s) => {
  if (!s) return '';
  if (s === 'manual') return 'set by hand';
  if (s === 'zocs') return 'from ZOCS tags';
  if (s === 'default') return 'default';
  if (s.startsWith('scrape:')) return `from ${s.slice(7)}`;
  return s;
};

export function ReviewPill({ review, label }) {
  const key = review || 'unclassified';
  return (
    <span className={`inline-flex items-center h-5 px-1.5 rounded text-[11px] font-medium ring-1 ring-inset whitespace-nowrap ${REVIEW_STYLE[key] || REVIEW_STYLE.unclassified}`}>
      {label || (key === 'unclassified' ? 'Not classified' : key)}
    </span>
  );
}

// One line for a list row / card: the category, and what is still open.
export function CategoryLine({ catalog, className = '' }) {
  if (!catalog || !catalog.category) {
    return <p className={`text-[11.5px] text-gray-400 ${className}`}>{catalog ? 'No category yet' : 'Not classified'}</p>;
  }
  return (
    <p className={`flex items-center gap-1.5 min-w-0 text-[11.5px] text-gray-500 ${className}`}>
      <span className="truncate" title={[catalog.category, ...catalog.subcategories].join(' · ')}>
        {catalog.category}{catalog.subcategories.length ? ` › ${catalog.subcategories.join(', ')}` : ''}
      </span>
      {catalog.review === 'needs_review' && <ReviewPill review="needs_review" label="Review" />}
    </p>
  );
}

function Chips({ items, tone = 'bg-gray-50 text-gray-700 ring-gray-200', extra = [] }) {
  return (
    <ul className="flex flex-wrap gap-1.5">
      {items.map((v) => (
        <li key={v} title={extra.includes(v) ? 'Not in the agreed taxonomy; stored as extra' : undefined}
          className={`inline-flex items-center h-6 px-2 rounded-md text-[12px] ring-1 ring-inset ${extra.includes(v) ? 'bg-violet-50 text-violet-700 ring-violet-200' : tone}`}>
          {v}
        </li>
      ))}
    </ul>
  );
}

export default function ClassificationView({ catalog }) {
  if (!catalog || !catalog.category) {
    return (
      <div>
        <p className="text-[12.5px] text-gray-400">This design has not been classified yet.</p>
        {catalog?.reasons?.length > 0 && <ReasonList reasons={catalog.reasons} />}
      </div>
    );
  }
  return (
    <div className="space-y-3.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[14px] font-semibold text-gray-900">{catalog.category}</span>
        <ReviewPill review={catalog.review} label={catalog.review_label} />
        {catalog.sources?.category && <span className="text-[11.5px] text-gray-400">{SOURCE_TEXT(catalog.sources.category)}</span>}
      </div>
      <Row label="Sub-categories" source={SOURCE_TEXT(catalog.sources?.subcategories)}>
        {catalog.subcategories.length ? <Chips items={catalog.subcategories} tone="bg-sky-50 text-sky-800 ring-sky-200" />
          : <span className="text-[12.5px] text-gray-400">none</span>}
      </Row>
      <Row label="Occasions" source={SOURCE_TEXT(catalog.sources?.occasions)}>
        {catalog.occasions.length ? <Chips items={catalog.occasions} tone="bg-orange-50 text-orange-800 ring-orange-200" />
          : <span className="text-[12.5px] text-gray-400">none</span>}
      </Row>
      {catalog.attributes.map((a) => (
        <Row key={a.key} label={a.label} source={SOURCE_TEXT(a.source)}>
          <Chips items={a.values} extra={a.extra} />
        </Row>
      ))}
      {catalog.reasons?.length > 0 && <ReasonList reasons={catalog.reasons} />}
      {catalog.manual && <p className="text-[11.5px] text-gray-400">Set by {catalog.manual.by}.</p>}
    </div>
  );
}

function Row({ label, source, children }) {
  return (
    <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-x-3 items-start">
      <div className="pt-0.5">
        <p className="text-[12px] font-medium text-gray-500">{label}</p>
        {source && <p className="text-[10.5px] text-gray-400">{source}</p>}
      </div>
      <div>{children}</div>
    </div>
  );
}

export function ReasonList({ reasons }) {
  return (
    <ul className="mt-1 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 space-y-0.5">
      {reasons.map((r) => (
        <li key={r} className="flex gap-1.5 text-[12px] text-amber-800">
          <CircleAlert size={13} className="shrink-0 mt-0.5" aria-hidden />{r}
        </li>
      ))}
    </ul>
  );
}
